import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Lightning, 
  ArrowsClockwise, 
  CheckCircle, 
  Circle,
  CalendarBlank,
  Target,
  PencilSimple,
  Quotes,
  Clock,
  ChatCircle,
  Rocket,
  ChartLineUp,
  Copy,
  ArrowRight,
  ArrowCounterClockwise,
  Trash
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShareButton } from '../components/ShareButton';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const industries = ['E-commerce', 'SaaS', 'Service Business', 'Personal Brand', 'Fashion', 'Beauty', 'Health & Wellness', 'Tech', 'Food & Beverage', 'Education', 'Coaching', 'Real Estate', 'Finance'];
const goals = ['Increase Sales', 'Build Engagement', 'Generate Leads', 'Grow Traffic', 'Build Awareness', 'Launch Product'];
const platforms = ['Instagram', 'TikTok', 'LinkedIn', 'X (Twitter)', 'Facebook', 'YouTube', 'Blog'];
const tones = ['Professional', 'Casual', 'Witty', 'Inspirational', 'Educational', 'Bold', 'Friendly', 'Luxurious'];

export const DailyPlanPage = () => {
  const navigate = useNavigate();
  const [businessType, setBusinessType] = useState('');
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [tone, setTone] = useState('');
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [priorities, setPriorities] = useState('');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [copiedField, setCopiedField] = useState(null);
  const [pageTab, setPageTab] = useState('create');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/daily-plan/history`);
      setHistory(data.plans || []);
    } catch {}
    setHistoryLoading(false);
  }, []);

  useEffect(() => { if (pageTab === 'history') loadHistory(); }, [pageTab, loadHistory]);

  const reloadFromHistory = (item) => {
    const inputs = item.inputs || {};
    if (inputs.business_type) setBusinessType(inputs.business_type);
    if (inputs.industry) setIndustry(inputs.industry);
    if (inputs.goals) setSelectedGoals(inputs.goals);
    if (inputs.platforms) setSelectedPlatforms(inputs.platforms);
    if (item.plan) setPlan(item.plan);
    setPageTab('create');
    toast.success('Loaded from history');
  };

  const toggleGoal = (goal) => {
    setSelectedGoals(prev => 
      prev.includes(goal) ? prev.filter(g => g !== goal) : [...prev, goal]
    );
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const generatePlan = async () => {
    if (!businessType || !industry || selectedGoals.length === 0 || selectedPlatforms.length === 0) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/daily-plan/generate`, {
        business_type: businessType,
        industry,
        goals: selectedGoals,
        platforms: selectedPlatforms,
        priorities,
        target_audience: targetAudience,
        tone: tone || 'Professional'
      });
      
      setPlan(data.plan || null);
      setCompletedTasks(new Set());
    } catch (err) {
      console.error('Failed to generate plan:', err);
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? 'Please fill in all required fields' : (typeof detail === 'string' ? detail : 'Generation failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = (taskId) => {
    setCompletedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <DashboardLayout>
      <div data-testid="daily-plan-page" className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <Lightning size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Daily Growth Plan</h1>
              <p className="text-zinc-400 text-sm">Get action items tailored to your brand</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {['create', 'history'].map(t => (
              <button key={t} onClick={() => setPageTab(t)} data-testid={`page-tab-${t}`}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: pageTab === t ? 'rgba(200,240,96,0.08)' : 'transparent',
                  border: `1px solid ${pageTab === t ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                  color: pageTab === t ? '#c8f060' : 'rgba(245,240,232,0.4)',
                }}>
                {t === 'create' ? 'Create' : 'History'}
              </button>
            ))}
          </div>
        </motion.div>

        {pageTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6 space-y-5 sticky top-24">
              <h2 className="font-medium font-['Instrument_Serif',serif]">Configure Your Plan</h2>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Business Type *</label>
                <input
                  type="text"
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  data-testid="business-type-input"
                  placeholder="e.g., Skincare brand, SaaS startup, Coaching business"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Industry *</label>
                <Select value={industry} onValueChange={setIndustry}>
                  <SelectTrigger data-testid="industry-select" className="w-full bg-zinc-900 border-white/10 text-white">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind} className="text-white hover:bg-white/10">
                        {ind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Audience</label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  data-testid="audience-input"
                  placeholder="e.g., Women 25-45, health-conscious professionals"
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Brand Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select" className="w-full bg-zinc-900 border-white/10 text-white">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {tones.map((t) => (
                      <SelectItem key={t} value={t} className="text-white hover:bg-white/10">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Goals * (select multiple)</label>
                <div className="flex flex-wrap gap-2">
                  {goals.map((goal) => (
                    <button
                      key={goal}
                      onClick={() => toggleGoal(goal)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        selectedGoals.includes(goal)
                          ? 'bg-white text-black'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Platforms * (select multiple)</label>
                <div className="flex flex-wrap gap-2">
                  {platforms.map((platform) => (
                    <button
                      key={platform}
                      onClick={() => togglePlatform(platform)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        selectedPlatforms.includes(platform)
                          ? 'bg-white text-black'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Current Priorities (optional)</label>
                <textarea
                  value={priorities}
                  onChange={(e) => setPriorities(e.target.value)}
                  data-testid="priorities-input"
                  placeholder="e.g., Launching new product next week, need more engagement"
                  rows={2}
                  className="w-full bg-zinc-900 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 px-4 py-3 focus:outline-none focus:ring-1 focus:ring-white/30 transition-all text-sm resize-none"
                />
              </div>

              {error && (
                <div className="text-sm rounded-xl px-4 py-2 bg-red-500/10 border border-red-500/20 text-red-400 mb-3">
                  {error}
                </div>
              )}

              <button
                onClick={generatePlan}
                disabled={loading || !businessType || !industry || selectedGoals.length === 0 || selectedPlatforms.length === 0}
                data-testid="generate-plan-btn"
                className="w-full bg-white text-black hover:bg-zinc-200 transition-all duration-200 rounded-full font-medium py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <ArrowsClockwise size={18} className="animate-spin" />
                    Generating Your Plan...
                  </>
                ) : (
                  <>
                    <Lightning size={18} />
                    Build my plan
                  </>
                )}
              </button>
            </div>
          </motion.div>

          {/* Plan Display */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {!plan ? (
              <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                <div className="text-center py-16">
                  <Lightning size={48} className="mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-lg font-medium mb-2">No plan generated yet</h3>
                  <p className="text-sm">Fill in your details — your daily growth plan appears here</p>
                </div>
              </div>
            ) : (
              <>
                {/* Today's Priority */}
                <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Target size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Today's Priority</h2>
                      <p className="text-zinc-500 text-xs">Your main focus for today</p>
                    </div>
                  </div>
                  <h3 className="text-xl font-medium mb-2">{plan.todays_priority?.focus}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{plan.todays_priority?.description}</p>
                </div>

                {/* Content Task */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <PencilSimple size={20} className="text-purple-400" />
                    </div>
                    <div>
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Content Task</h2>
                      <p className="text-zinc-500 text-xs">Your content creation assignment</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 mb-2 inline-block">
                          {plan.content_task?.format}
                        </span>
                        <h3 className="font-medium">{plan.content_task?.idea}</h3>
                      </div>
                    </div>
                    <p className="text-zinc-400 text-sm">{plan.content_task?.details}</p>
                    {/* Cross-link: Create this content now */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      {[
                        { label: 'Create Carousel', path: '/dashboard/carousel-creator', match: ['carousel','swipe','slide'] },
                        { label: 'Create Video', path: '/dashboard/ad-creator', match: ['video','reel','clip'] },
                        { label: 'Create Flyer', path: '/dashboard/flyer-creator', match: ['flyer','poster','graphic','image','template'] },
                        { label: 'Write Caption', path: '/dashboard/content-studio', match: ['caption','post','story','thread'] },
                      ].filter(link => {
                        const fmt = (plan.content_task?.format || '').toLowerCase();
                        const idea = (plan.content_task?.idea || '').toLowerCase();
                        return link.match.some(m => fmt.includes(m) || idea.includes(m));
                      }).slice(0, 2).map(link => (
                        <button key={link.path} onClick={() => navigate(link.path)}
                          data-testid={`create-now-${link.label.toLowerCase().replace(/\s/g,'-')}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:opacity-90"
                          style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
                          <ArrowRight size={11} /> {link.label}
                        </button>
                      ))}
                      {/* Always show generic create link */}
                      <button onClick={() => navigate('/dashboard/content-studio')}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:opacity-90"
                        style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <ArrowRight size={11} /> Open Content Studio
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hook & Caption */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Hook */}
                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                          <Quotes size={20} className="text-orange-400" />
                        </div>
                        <h2 className="font-medium font-['Instrument_Serif',serif]">Hook</h2>
                      </div>
                      <button
                        onClick={() => copyToClipboard(plan.hook, 'hook')}
                        className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      >
                        {copiedField === 'hook' ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                      </button>
                    </div>
                    <p className="text-zinc-200 font-medium">"{plan.hook}"</p>
                  </div>

                  {/* Posting Strategy */}
                  <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                        <Clock size={20} className="text-blue-400" />
                      </div>
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Posting Strategy</h2>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500">Platform:</span>
                        <span className="text-zinc-200">{plan.posting_strategy?.platform}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500">Format:</span>
                        <span className="text-zinc-200">{plan.posting_strategy?.format}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-500">Best time:</span>
                        <span className="text-zinc-200">{plan.posting_strategy?.best_time}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                        <PencilSimple size={20} className="text-green-400" />
                      </div>
                      <div>
                        <h2 className="font-medium font-['Instrument_Serif',serif]">Ready-to-Use Caption</h2>
                        <p className="text-zinc-500 text-xs">Copy and post</p>
                      </div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(plan.caption, 'caption')}
                      data-testid="copy-caption-btn"
                      className="flex items-center gap-2 px-3 py-1.5 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm"
                    >
                      {copiedField === 'caption' ? (
                        <>
                          <CheckCircle size={16} className="text-green-400" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          Copy
                        </>
                      )}
                    </button>
                    <ShareButton title="Daily Growth Plan" content={plan.caption} type="Daily Plan Caption" />
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <p className="text-zinc-200 text-sm whitespace-pre-line">{plan.caption}</p>
                  </div>
                </div>

                {/* Engagement Tasks */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
                      <ChatCircle size={20} className="text-pink-400" />
                    </div>
                    <div>
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Engagement Tasks</h2>
                      <p className="text-zinc-500 text-xs">Actions to increase engagement</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {plan.engagement_tasks?.map((task, index) => (
                      <div
                        key={index}
                        onClick={() => toggleTask(`engagement-${index}`)}
                        className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                          completedTasks.has(`engagement-${index}`) 
                            ? 'bg-white/5 border-white/5' 
                            : 'bg-zinc-800/50 border-white/10 hover:bg-zinc-800'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                            completedTasks.has(`engagement-${index}`) 
                              ? 'border-green-500 bg-green-500' 
                              : 'border-zinc-600'
                          }`}>
                            {completedTasks.has(`engagement-${index}`) && (
                              <CheckCircle size={12} className="text-white" weight="fill" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-medium text-sm mb-1 ${completedTasks.has(`engagement-${index}`) ? 'text-zinc-500 line-through' : 'text-white'}`}>
                              {task.task}
                            </h3>
                            <p className="text-zinc-500 text-xs">{task.details}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Growth Actions */}
                <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                      <Rocket size={20} className="text-yellow-400" />
                    </div>
                    <div>
                      <h2 className="font-medium font-['Instrument_Serif',serif]">Growth Actions</h2>
                      <p className="text-zinc-500 text-xs">Strategies beyond posting</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {plan.growth_actions?.map((action, index) => (
                      <div
                        key={index}
                        onClick={() => toggleTask(`growth-${index}`)}
                        className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                          completedTasks.has(`growth-${index}`) 
                            ? 'bg-white/5 border-white/5' 
                            : 'bg-zinc-800/50 border-white/10 hover:bg-zinc-800'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                            completedTasks.has(`growth-${index}`) 
                              ? 'border-green-500 bg-green-500' 
                              : 'border-zinc-600'
                          }`}>
                            {completedTasks.has(`growth-${index}`) && (
                              <CheckCircle size={12} className="text-white" weight="fill" />
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-medium text-sm mb-1 ${completedTasks.has(`growth-${index}`) ? 'text-zinc-500 line-through' : 'text-white'}`}>
                              {action.action}
                            </h3>
                            <p className="text-zinc-500 text-xs">{action.details}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expected Impact */}
                <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                      <ChartLineUp size={20} className="text-green-400" />
                    </div>
                    <h2 className="font-medium font-['Instrument_Serif',serif]">Expected Impact</h2>
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed">{plan.expected_impact}</p>
                </div>

                {/* Regenerate Button */}
                <button
                  onClick={generatePlan}
                  disabled={loading}
                  className="w-full py-2.5 text-center rounded-full border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
                  Refresh my plan
                </button>
              </>
            )}
          </motion.div>
        </div>
        )}

        {/* History Tab */}
        {pageTab === 'history' && (
          <div className="rounded-[14px] p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="font-medium font-['Instrument_Serif',serif] mb-4">Plan History</h2>
            {historyLoading ? (
              <div className="flex justify-center py-12"><div className="w-5 h-5 border-2 border-white/10 border-t-white/40 rounded-full animate-spin" /></div>
            ) : history.length === 0 ? (
              <div className="text-center py-12">
                <Clock size={32} className="mx-auto mb-3 opacity-20" style={{ color: '#f5f0e8' }} />
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No plans generated yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((item, i) => (
                  <div key={item.id || i} onClick={() => reloadFromHistory(item)}
                    className="group rounded-xl p-4 cursor-pointer transition-all hover:ring-1 hover:ring-white/10"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                    data-testid={`history-item-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#f5f0e8' }}>
                          {item.inputs?.business_type || item.inputs?.industry || 'Daily Plan'}
                        </p>
                        <p className="text-xs mt-1" style={{ color: 'rgba(245,240,232,0.25)' }}>
                          {item.inputs?.platforms?.join(', ')} &middot; {item.inputs?.goals?.join(', ')}
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: 'rgba(245,240,232,0.15)' }}>
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                      <ArrowCounterClockwise size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#c8f060' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DailyPlanPage;
